import {
  parseLoomAcceptedMeshHeader,
  type LoomAcceptedMeshHeader,
} from "./loomAcceptedMeshContract";
import {
  assertAcceptedGeometryMatchesLegacyPanel,
  decodeLoomAcceptedMeshContract,
  type LoomAcceptedMeshContract,
} from "./loomAcceptedMeshDecoder";
import {
  verifyLoomAcceptedMeshIntegrity,
  type LoomVerifiedAcceptedMeshContract,
} from "./loomAcceptedMeshIntegrity";
import { verifyLoomDefinitionIntegrity } from "./loomNativeHybridDefinitionIntegrity";
import {
  validateLoomNativeHybridArrayDescriptors,
  type LoomNativeHybridArrayDescriptor,
  type LoomNativeHybridDType,
} from "./loomNativeHybridArrayDescriptors";

export { acceptedGeometryForPanelizeTris } from "./loomAcceptedMeshDecoder";
export type { LoomVerifiedAcceptedMeshContract } from "./loomAcceptedMeshIntegrity";
export type {
  LoomNativeHybridArrayDescriptor,
  LoomNativeHybridDType,
} from "./loomNativeHybridArrayDescriptors";

const MAGIC = "LOOMHYB1";
const FORMAT_VERSION = 1;
const PREFIX_BYTES = 16;

export const LOOM_NATIVE_HYBRID_ASSET_URL =
  "/internal/parametric-geometry/loom-native-hybrid-v1.bin";

export interface LoomNativeHybridQcReceipt {
  vertices: number;
  faces: number;
  watertight: boolean;
  boundary_edges: number;
  manifold: boolean;
  nonmanifold_edges: number;
  winding_consistent: boolean;
  euler_characteristic: number;
  euler_expected: number;
  self_intersections: number;
  volume: number | null;
  area: number;
  bbox: number[][];
  passed: boolean;
}

export interface LoomNativeHybridParameterDefinition {
  id: string;
  label: string;
  value: number;
  minimum: number;
  maximum: number;
  displayUnit: string;
  jacobianUnit: string;
}

export interface LoomNativeHybridElementQualityReceipt {
  maxEdgeM: number;
  p99AspectRatio: number;
  maxAspectRatio: number;
  minAngleDeg: number;
  thresholds: {
    maximumEdgeM: number;
    maximumP99AspectRatio: number;
    maximumAspectRatio: number;
    minimumAngleDeg: number;
  };
  passed: boolean;
}

export interface LoomNativeHybridParameterLineFamily {
  segmentsArray: string;
  segmentCount: number;
  provenance: string;
}

export interface LoomNativeHybridKuttaPairReceipt {
  connectorEdge: [number, number];
  adjacentFaces: [number, number];
  exactSeamUpperSurfaceVertex: number;
  exactSeamLowerSurfaceVertex: number;
  upperFace: number;
  lowerFace: number;
  upperDotWakeNormal: number;
  lowerDotWakeNormal: number;
  rootSeamGapM: number;
  chainContinuous: boolean;
  allEdgesTwoSided: boolean;
  passed: boolean;
}

export interface LoomNativeHybridAeroValidationReceipt {
  passed: boolean;
  method: string;
  reason: string;
}

export interface LoomNativeHybridHeader {
  schema: "loom.native-hybrid-showcase";
  version: 1;
  title: string;
  accepted: boolean;
  coordinateSystem: {
    axes: "x aft, y starboard, z up";
    lengthUnit: "m";
    indexBase: 0;
  };
  provenance: {
    generator: string;
    geometryBuilder: string;
    seamBuilder: string;
    jacobianBuilder: string;
  };
  resolution: Record<string, number>;
  parameters: {
    order: string[];
    definitions: LoomNativeHybridParameterDefinition[];
    frozenTopology: true;
    rebuildRequiredWhenTopologyChanges: true;
    wingShiftM: number[];
    fuselageStations: Array<Record<string, number | string>>;
    definitionRevision?: string;
    authoritativeDefinition?: Record<string, unknown>;
  };
  surface: {
    topology: "triangular boundary surface; not a tetrahedral volume mesh";
    vertexCount: number;
    faceCount: number;
    positionsArray: string;
    facesArray: string;
    geometryRevision?: string;
    topologyRevision?: string;
    parameterLines: {
      indexSpace: "surface.positions";
      edgeFilter: string;
      wingChordwiseU: LoomNativeHybridParameterLineFamily;
      wingSpanwiseV: LoomNativeHybridParameterLineFamily;
      fuselageCircumferentialTheta: LoomNativeHybridParameterLineFamily;
      fuselageAxialX: LoomNativeHybridParameterLineFamily;
    };
    maxVehicleFieldResidualM: number;
    qc: LoomNativeHybridQcReceipt;
    elementQuality: LoomNativeHybridElementQualityReceipt;
    accepted: boolean;
  };
  seam: {
    sourceRingCount: number;
    meshUniqueRingCount: number;
    maxCoordinateGapM: number;
    maxWingFieldResidualM: number;
    maxFuselageFieldResidualM: number;
    wingVertexIndicesArray: string;
    fuselageVertexIndicesArray: string;
  };
  panel: {
    topology: "triangular boundary panels; not tetrahedral volume elements";
    vertexCount: number;
    faceCount: number;
    panelCount: number;
    droppedDegeneratePanels: number;
    trailingEdgeCount: number;
    positionsArray: string;
    facesArray: string;
    panelOrderArray: string;
    neighboursArray: string;
    trailingEdgeEdgesArray: string;
    trailingEdgeUpperFacesArray: string;
    trailingEdgeLowerFacesArray: string;
    faceOrderIsPanelOrder: true;
    trailingEdgeOrdering: "root-to-tip, increasing y" | "root-to-tip, increasing source span parameter";
    trailingEdgeRootSeamGapM: number;
    trailingEdgeConnectorLengthM: number;
    trailingEdgeEndpointToleranceM: number;
    trailingEdgeChainContinuous: boolean;
    trailingEdgeAllEdgesTwoSided: boolean;
    trailingEdgeRootOnSharedSeam: boolean;
    trailingEdgeKuttaPairVerified: boolean;
    trailingEdgeKuttaPairReceipt: LoomNativeHybridKuttaPairReceipt;
    aeroValidation: LoomNativeHybridAeroValidationReceipt;
    trailingEdgeProvenance: string;
    qc: LoomNativeHybridQcReceipt;
    elementQuality: LoomNativeHybridElementQualityReceipt;
    accepted: boolean;
  };
  jacobian: {
    parameterOrder: string[];
    dynamicVertexCount: number;
    shape: number[];
    vertexIndicesArray: string;
    valuesArray: string;
    storage: "row-major [dynamic vertex, xyz, parameter]";
    validation: {
      method: string;
      topologyStable: boolean;
      maxBaseVertexReconstructionM: number;
      epsilonDisplayUnits: Record<string, number>;
      relativeL2ByParameter: Record<string, number>;
      maxAbsErrorByParameter: Record<string, number>;
      maximumRelativeL2: number;
      maximumAbsoluteError: number;
    };
  };
  arrays: Record<string, LoomNativeHybridArrayDescriptor>;
  payloadSha256: string;
  limitations: string[];
  acceptedGeometry: LoomAcceptedMeshHeader;
}

export interface LoomNativeHybridAsset {
  integrity: { status: "unverified" } | {
    status: "verified";
    payloadSha256: true;
    acceptedMeshRevisions: true;
    sourceContainerRevision: string;
  };
  header: LoomNativeHybridHeader;
  acceptedGeometry: LoomAcceptedMeshContract;
  surface: {
    positions: Float32Array;
    faces: Uint32Array;
    parameterLines: {
      wingChordwiseU: Uint32Array;
      wingSpanwiseV: Uint32Array;
      fuselageCircumferentialTheta: Uint32Array;
      fuselageAxialX: Uint32Array;
    };
  };
  seam: {
    wingVertexIndices: Uint32Array;
    fuselageVertexIndices: Uint32Array;
  };
  panel: {
    positions: Float32Array;
    faces: Uint32Array;
    order: Uint32Array;
    neighbours: Uint32Array;
    trailingEdgeEdges: Uint32Array;
    trailingEdgeUpperFaces: Uint32Array;
    trailingEdgeLowerFaces: Uint32Array;
  };
  jacobian: {
    vertexIndices: Uint32Array;
    values: Float32Array;
  };
}

export interface LoomVerifiedNativeHybridAsset extends LoomNativeHybridAsset {
  integrity: {
    status: "verified";
    payloadSha256: true;
    acceptedMeshRevisions: true;
    sourceContainerRevision: string;
  };
  acceptedGeometry: LoomVerifiedAcceptedMeshContract;
}

function assertRecord(value: unknown, label: string): asserts value is Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
}

function assertFiniteNumber(value: unknown, label: string): asserts value is number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${label} must be finite`);
  }
}

function parseHeader(bytes: Uint8Array): LoomNativeHybridHeader {
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(bytes));
  } catch (error) {
    throw new Error(`Invalid Loom native-hybrid header JSON: ${String(error)}`);
  }
  assertRecord(parsed, "Loom native-hybrid header");
  if (parsed.schema !== "loom.native-hybrid-showcase" || parsed.version !== FORMAT_VERSION) {
    throw new Error("Unsupported Loom native-hybrid schema or version");
  }
  if (typeof parsed.payloadSha256 !== "string" || !/^[0-9a-f]{64}$/.test(parsed.payloadSha256)) {
    throw new Error("Loom native-hybrid payload SHA-256 receipt is invalid");
  }
  assertRecord(parsed.arrays, "Loom native-hybrid arrays");
  assertRecord(parsed.panel, "Loom native-hybrid panel receipt");
  assertRecord(parsed.surface, "Loom native-hybrid surface receipt");
  assertRecord(parsed.surface.qc, "Loom native-hybrid surface QC receipt");
  assertRecord(parsed.panel.qc, "Loom native-hybrid panel QC receipt");
  const acceptedGeometry = parseLoomAcceptedMeshHeader(parsed.acceptedGeometry, {
    arrays: parsed.arrays,
    headerAccepted: parsed.accepted,
    surfaceAccepted: parsed.surface.accepted,
    surfaceQcPassed: parsed.surface.qc.passed,
    surfacePositionsArray: parsed.surface.positionsArray,
    surfaceFacesArray: parsed.surface.facesArray,
    surfaceVertexCount: parsed.surface.vertexCount,
    surfaceFaceCount: parsed.surface.faceCount,
    panelAccepted: parsed.panel.accepted,
    panelQcPassed: parsed.panel.qc.passed,
    panelVertexCount: parsed.panel.vertexCount,
    panelFaceCount: parsed.panel.faceCount,
    panelPositionsArray: parsed.panel.positionsArray,
    panelFacesArray: parsed.panel.facesArray,
  });
  parsed.acceptedGeometry = acceptedGeometry;
  if (acceptedGeometry.schema === "loom.accepted-mesh/v3") {
    const union = acceptedGeometry.unionReceipt;
    if (!union || parsed.surface.geometryRevision !== union.surfaceGeometryRevision
        || parsed.surface.topologyRevision !== union.surfaceTopologyRevision) {
      throw new Error("Loom v3 exact-surface receipt is stale or missing");
    }
  }
  if (parsed.panel.trailingEdgeKuttaPairVerified !== true
      || parsed.panel.trailingEdgeChainContinuous !== true
      || parsed.panel.trailingEdgeAllEdgesTwoSided !== true) {
    throw new Error("Loom native-hybrid Kutta chain is missing or unverified");
  }
  assertRecord(parsed.panel.trailingEdgeKuttaPairReceipt, "Loom native-hybrid Kutta-pair receipt");
  const pair = parsed.panel.trailingEdgeKuttaPairReceipt;
  if (pair.passed !== true || pair.chainContinuous !== true || pair.allEdgesTwoSided !== true) {
    throw new Error("Loom native-hybrid Kutta-pair receipt did not pass");
  }
  for (const name of ["upperDotWakeNormal", "lowerDotWakeNormal", "rootSeamGapM"] as const) {
    assertFiniteNumber(pair[name], `Loom native-hybrid Kutta-pair receipt.${name}`);
  }
  assertRecord(parsed.panel.aeroValidation, "Loom native-hybrid aero validation");
  if (typeof parsed.panel.aeroValidation.passed !== "boolean"
      || typeof parsed.panel.aeroValidation.method !== "string"
      || typeof parsed.panel.aeroValidation.reason !== "string") {
    throw new Error("Loom native-hybrid aero validation is invalid");
  }
  return parsed as unknown as LoomNativeHybridHeader;
}

function descriptor(
  header: LoomNativeHybridHeader,
  name: string,
  dtype: LoomNativeHybridDType,
): LoomNativeHybridArrayDescriptor {
  const value = header.arrays[name];
  if (!value) throw new Error(`Loom native-hybrid array is missing: ${name}`);
  if (value.dtype !== dtype) throw new Error(`Loom native-hybrid array ${name} is not ${dtype}`);
  return value;
}

function checkedOffset(
  buffer: ArrayBuffer,
  payloadOffset: number,
  value: LoomNativeHybridArrayDescriptor,
): number {
  const offset = payloadOffset + value.byteOffset;
  const end = offset + value.count * 4;
  if (offset % 4 !== 0 || end > buffer.byteLength) {
    throw new Error("Loom native-hybrid array is misaligned or outside the payload");
  }
  return offset;
}

export function decodeLoomNativeHybridAsset(buffer: ArrayBuffer): LoomNativeHybridAsset {
  if (buffer.byteLength < PREFIX_BYTES) throw new Error("Loom native-hybrid asset is truncated");
  const prefix = new Uint8Array(buffer, 0, 8);
  if (new TextDecoder().decode(prefix) !== MAGIC) throw new Error("Invalid Loom native-hybrid magic");
  const view = new DataView(buffer);
  if (view.getUint32(8, true) !== FORMAT_VERSION) throw new Error("Unsupported Loom native-hybrid format");
  const headerLength = view.getUint32(12, true);
  const paddedHeaderLength = (headerLength + 3) & ~3;
  const payloadOffset = PREFIX_BYTES + paddedHeaderLength;
  if (headerLength === 0 || payloadOffset > buffer.byteLength) {
    throw new Error("Loom native-hybrid header is truncated");
  }
  const header = parseHeader(new Uint8Array(buffer, PREFIX_BYTES, headerLength));
  validateLoomNativeHybridArrayDescriptors(
    header.arrays as Record<string, unknown>, buffer.byteLength - payloadOffset,
  );
  const f32 = (name: string) => {
    const value = descriptor(header, name, "float32");
    return new Float32Array(buffer, checkedOffset(buffer, payloadOffset, value), value.count);
  };
  const u32 = (name: string) => {
    const value = descriptor(header, name, "uint32");
    return new Uint32Array(buffer, checkedOffset(buffer, payloadOffset, value), value.count);
  };
  const acceptedGeometry = decodeLoomAcceptedMeshContract(
    header.acceptedGeometry, { f32, u32 }, {
      facesArray: header.surface.facesArray,
      vertexCount: header.surface.vertexCount,
    },
  );
  const surfacePositions = f32(header.surface.positionsArray);
  const surfaceFaces = u32(header.surface.facesArray);
  const trailingEdgeEdges = u32(header.panel.trailingEdgeEdgesArray);
  const trailingEdgeUpperFaces = u32(header.panel.trailingEdgeUpperFacesArray);
  const trailingEdgeLowerFaces = u32(header.panel.trailingEdgeLowerFacesArray);
  assertAcceptedGeometryMatchesLegacyPanel(acceptedGeometry, {
    trailingEdgeEdges,
    trailingEdgeUpperFaces,
    trailingEdgeLowerFaces,
  });

  return {
    integrity: { status: "unverified" },
    header,
    acceptedGeometry,
    surface: {
      positions: surfacePositions,
      faces: surfaceFaces,
      parameterLines: {
        wingChordwiseU: u32(header.surface.parameterLines.wingChordwiseU.segmentsArray),
        wingSpanwiseV: u32(header.surface.parameterLines.wingSpanwiseV.segmentsArray),
        fuselageCircumferentialTheta: u32(
          header.surface.parameterLines.fuselageCircumferentialTheta.segmentsArray,
        ),
        fuselageAxialX: u32(header.surface.parameterLines.fuselageAxialX.segmentsArray),
      },
    },
    seam: {
      wingVertexIndices: u32(header.seam.wingVertexIndicesArray),
      fuselageVertexIndices: u32(header.seam.fuselageVertexIndicesArray),
    },
    panel: {
      positions: acceptedGeometry.positions,
      faces: acceptedGeometry.faces,
      order: u32(header.panel.panelOrderArray),
      neighbours: u32(header.panel.neighboursArray),
      trailingEdgeEdges,
      trailingEdgeUpperFaces,
      trailingEdgeLowerFaces,
    },
    jacobian: {
      vertexIndices: u32(header.jacobian.vertexIndicesArray),
      values: f32(header.jacobian.valuesArray),
    },
  };
}

export async function loadLoomNativeHybridAsset(
  url = LOOM_NATIVE_HYBRID_ASSET_URL,
  fetcher: typeof fetch = fetch,
): Promise<LoomVerifiedNativeHybridAsset> {
  const response = await fetcher(url);
  if (!response.ok) throw new Error(`Unable to load Loom native-hybrid asset (${response.status})`);
  const buffer = await response.arrayBuffer();
  return verifyLoomNativeHybridAssetBuffer(buffer);
}

export async function verifyLoomNativeHybridAssetBuffer(
  buffer: ArrayBuffer,
): Promise<LoomVerifiedNativeHybridAsset> {
  const asset = decodeLoomNativeHybridAsset(buffer);
  if (!globalThis.crypto?.subtle) throw new Error("Web Crypto is unavailable for Loom asset integrity validation");
  const headerLength = new DataView(buffer).getUint32(12, true);
  const payloadOffset = PREFIX_BYTES + ((headerLength + 3) & ~3);
  const [payloadDigest, sourceDigest] = await Promise.all([
    globalThis.crypto.subtle.digest("SHA-256", buffer.slice(payloadOffset)),
    globalThis.crypto.subtle.digest("SHA-256", buffer),
  ]);
  const hex = (digest: ArrayBuffer) => Array.from(new Uint8Array(digest),
    (value) => value.toString(16).padStart(2, "0")).join("");
  const actual = hex(payloadDigest);
  if (actual !== asset.header.payloadSha256) throw new Error("Loom native-hybrid payload SHA-256 mismatch");
  await verifyLoomDefinitionIntegrity(
    asset.header.acceptedGeometry,
    asset.header.parameters as unknown as Record<string, unknown>,
  );
  const acceptedGeometry = await verifyLoomAcceptedMeshIntegrity({
    acceptedGeometry: asset.acceptedGeometry,
    exactSurface: asset.surface,
  });
  return {
    ...asset,
    integrity: { status: "verified", payloadSha256: true, acceptedMeshRevisions: true,
      sourceContainerRevision: `sha256:${hex(sourceDigest)}` },
    acceptedGeometry,
  };
}
